<template>
  <div class="settings-employment-ref-section">
    <SettingsSectionHeader
      title="Employment reference"
      subtitle="Employment types and employing companies for employee profiles"
      icon="pi-id-card"
    >
      <template #actions>
        <Button
          icon="pi pi-refresh"
          label="Refresh"
          size="small"
          severity="secondary"
          outlined
          :loading="loading"
          @click="loadReference"
        />
      </template>
    </SettingsSectionHeader>

    <div class="settings-employment-ref-section__body">
      <div v-if="loading && !employmentTypes.length && !companies.length" class="settings-employment-ref-section__state">
        <ProgressSpinner style="width: 2rem; height: 2rem" strokeWidth="4" />
      </div>

      <Message v-else-if="error" severity="error" :closable="false" class="settings-employment-ref-section__message">
        <div class="flex flex-col sm:flex-row sm:items-center gap-3">
          <span class="flex-1">{{ error }}</span>
          <Button label="Retry" size="small" severity="danger" outlined @click="loadReference" />
        </div>
      </Message>

      <Tabs v-else v-model:value="activeTab" class="settings-employment-ref-section__tabs">
        <TabList class="settings-employment-ref-section__tablist">
          <Tab value="types">
            <span class="inline-flex items-center gap-2">
              <i class="pi pi-id-card text-xs" aria-hidden="true" />
              Employment types
            </span>
          </Tab>
          <Tab value="companies">
            <span class="inline-flex items-center gap-2">
              <i class="pi pi-building text-xs" aria-hidden="true" />
              Companies
            </span>
          </Tab>
        </TabList>

        <TabPanels>
          <!-- Types -->
          <TabPanel value="types">
            <div class="settings-employment-ref-section__panel">
              <section class="settings-employment-ref-section__quick-add" aria-label="Quick add employment type">
                <div class="settings-employment-ref-section__quick-add-title">Quick add</div>
                <form class="settings-employment-ref-section__quick-add-form settings-employment-ref-section__quick-add-form--types" @submit.prevent="handleTypeQuickAdd">
                  <FloatLabel variant="on" class="settings-employment-ref-section__quick-add-field">
                    <InputText
                      id="employment-type-quick-code"
                      v-model="typeQuickAdd.code"
                      maxlength="50"
                      autocomplete="off"
                      :disabled="typeCreating"
                      class="w-full rounded-xl"
                    />
                    <label for="employment-type-quick-code" class="text-sm font-medium">
                      Code <span class="text-red-500">*</span>
                    </label>
                  </FloatLabel>
                  <FloatLabel variant="on" class="settings-employment-ref-section__quick-add-field">
                    <InputText
                      id="employment-type-quick-label"
                      v-model="typeQuickAdd.label"
                      maxlength="100"
                      autocomplete="off"
                      :disabled="typeCreating"
                      class="w-full rounded-xl"
                    />
                    <label for="employment-type-quick-label" class="text-sm font-medium">Display label</label>
                  </FloatLabel>
                  <Button
                    type="submit"
                    label="Add"
                    icon="pi pi-plus"
                    :loading="typeCreating"
                    :disabled="!typeQuickAdd.code?.trim()"
                  />
                </form>
              </section>

              <SettingsOrgReferenceToolbar
                v-model:show-inactive="showInactive"
                v-model:search-query="typeSearchQuery"
                :count-label="typesCountLabel"
                inactive-input-id="employment-ref-types-show-inactive"
                search-placeholder="Search types..."
                :loading="loading"
              />

              <div v-if="!filteredTypes.length" class="settings-employment-ref-section__empty">
                <i class="pi pi-id-card settings-employment-ref-section__empty-icon" aria-hidden="true" />
                <p>{{ typesEmptyMessage }}</p>
                <Button
                  v-if="typeSearchQuery"
                  label="Clear search"
                  size="small"
                  severity="secondary"
                  text
                  @click="typeSearchQuery = ''"
                />
              </div>

              <DataTable
                v-else
                :value="filteredTypes"
                size="small"
                dataKey="id"
                sort-mode="single"
                removable-sort
                :sort-field="typeSortField"
                :sort-order="typeSortOrder"
                @sort="onTypeSort"
                class="settings-employment-ref-section__table"
                :rowClass="rowClass"
              >
                <Column field="label" header="Label" sortable>
                  <template #body="{ data }">
                    <div class="flex flex-col gap-0.5">
                      <span class="font-medium">{{ data.label }}</span>
                      <span v-if="data.is_system" class="text-[10px] uppercase tracking-wide text-amber-600 dark:text-amber-400">
                        System
                      </span>
                    </div>
                  </template>
                </Column>
                <Column field="code" header="Code" sortable style="width: 8rem">
                  <template #body="{ data }">
                    <span class="font-mono text-sm">{{ data.code }}</span>
                  </template>
                </Column>
                <Column header="Employees" style="width: 6.5rem">
                  <template #body="{ data }">
                    {{ data.employee_count ?? 0 }}
                  </template>
                </Column>
                <Column v-if="showInactive" header="Status" style="width: 6.5rem">
                  <template #body="{ data }">
                    <SettingsOrgReferenceStatusTag :item="data" />
                  </template>
                </Column>
                <Column header="Actions" style="width: 11rem">
                  <template #body="{ data }">
                    <div class="crc-table-row-actions">
                      <Button
                        icon="pi pi-pencil"
                        size="small"
                        severity="secondary"
                        outlined
                        rounded
                        class="crc-table-action-btn"
                        v-tooltip.top="'Edit'"
                  aria-label="Edit"
                        :disabled="mutating"
                        @click="openTypeEdit(data)"
                      />
                      <Button
                        v-if="isOrgReferenceActive(data)"
                        icon="pi pi-ban"
                        size="small"
                        severity="warn"
                        outlined
                        rounded
                        class="crc-table-action-btn"
                        v-tooltip.top="'Deactivate'"
                  aria-label="Deactivate"
                        :disabled="mutating"
                        @click="confirmTypeDeactivate(data)"
                      />
                      <Button
                        icon="pi pi-trash"
                        size="small"
                        severity="danger"
                        outlined
                        rounded
                        class="crc-table-action-btn"
                        v-tooltip.top="'Delete'"
                  aria-label="Delete"
                        :disabled="mutating"
                        @click="confirmTypeDelete(data)"
                      />
                    </div>
                  </template>
                </Column>
              </DataTable>
            </div>
          </TabPanel>

          <!-- Companies -->
          <TabPanel value="companies">
            <div class="settings-employment-ref-section__panel">
              <section class="settings-employment-ref-section__quick-add" aria-label="Quick add company">
                <div class="settings-employment-ref-section__quick-add-title">Quick add</div>
                <form class="settings-employment-ref-section__quick-add-form settings-employment-ref-section__quick-add-form--companies" @submit.prevent="handleCompanyQuickAdd">
                  <FloatLabel variant="on" class="settings-employment-ref-section__quick-add-field">
                    <InputText
                      id="company-quick-name"
                      v-model="companyQuickAdd.name"
                      maxlength="190"
                      autocomplete="off"
                      :disabled="companyCreating"
                      class="w-full rounded-xl"
                    />
                    <label for="company-quick-name" class="text-sm font-medium">
                      Company name <span class="text-red-500">*</span>
                    </label>
                  </FloatLabel>
                  <div class="settings-employment-ref-section__quick-add-field">
                    <MultiSelect
                      id="company-quick-types"
                      v-model="companyQuickAdd.employment_type_codes"
                      :options="activeTypeSelectOptions"
                      option-label="label"
                      option-value="value"
                      display="chip"
                      filter
                      :disabled="companyCreating"
                      class="w-full"
                      placeholder="Employment types *"
                    />
                  </div>
                  <Button
                    type="submit"
                    label="Add"
                    icon="pi pi-plus"
                    :loading="companyCreating"
                    :disabled="!companyQuickAdd.name?.trim() || !companyQuickAdd.employment_type_codes.length"
                  />
                </form>
              </section>

              <SettingsOrgReferenceToolbar
                v-model:show-inactive="showInactive"
                v-model:search-query="companySearchQuery"
                :count-label="companiesCountLabel"
                inactive-input-id="employment-ref-companies-show-inactive"
                search-placeholder="Search companies..."
                :loading="loading"
              />

              <div v-if="!filteredCompanies.length" class="settings-employment-ref-section__empty">
                <i class="pi pi-building settings-employment-ref-section__empty-icon" aria-hidden="true" />
                <p>{{ companiesEmptyMessage }}</p>
                <Button
                  v-if="companySearchQuery"
                  label="Clear search"
                  size="small"
                  severity="secondary"
                  text
                  @click="companySearchQuery = ''"
                />
              </div>

              <DataTable
                v-else
                :value="filteredCompanies"
                size="small"
                dataKey="id"
                sort-mode="single"
                removable-sort
                :sort-field="companySortField"
                :sort-order="companySortOrder"
                @sort="onCompanySort"
                class="settings-employment-ref-section__table"
                :rowClass="rowClass"
              >
                <Column field="name" header="Name" sortable>
                  <template #body="{ data }">
                    <span class="font-medium">{{ data.name }}</span>
                  </template>
                </Column>
                <Column header="Employment types" style="min-width: 10rem">
                  <template #body="{ data }">
                    <span class="text-surface-600 dark:text-surface-400 text-sm">
                      {{ formatCompanyTypes(data) }}
                    </span>
                  </template>
                </Column>
                <Column header="Employees" style="width: 6.5rem">
                  <template #body="{ data }">
                    {{ data.employee_count ?? 0 }}
                  </template>
                </Column>
                <Column v-if="showInactive" header="Status" style="width: 6.5rem">
                  <template #body="{ data }">
                    <SettingsOrgReferenceStatusTag :item="data" />
                  </template>
                </Column>
                <Column header="Actions" style="width: 11rem">
                  <template #body="{ data }">
                    <div class="crc-table-row-actions">
                      <Button
                        icon="pi pi-pencil"
                        size="small"
                        severity="secondary"
                        outlined
                        rounded
                        class="crc-table-action-btn"
                        v-tooltip.top="'Edit'"
                  aria-label="Edit"
                        :disabled="mutating"
                        @click="openCompanyEdit(data)"
                      />
                      <Button
                        v-if="isOrgReferenceActive(data)"
                        icon="pi pi-ban"
                        size="small"
                        severity="warn"
                        outlined
                        rounded
                        class="crc-table-action-btn"
                        v-tooltip.top="'Deactivate'"
                  aria-label="Deactivate"
                        :disabled="mutating"
                        @click="confirmCompanyDeactivate(data)"
                      />
                      <Button
                        icon="pi pi-trash"
                        size="small"
                        severity="danger"
                        outlined
                        rounded
                        class="crc-table-action-btn"
                        v-tooltip.top="'Delete'"
                  aria-label="Delete"
                        :disabled="mutating"
                        @click="confirmCompanyDelete(data)"
                      />
                    </div>
                  </template>
                </Column>
              </DataTable>
            </div>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </div>

    <EmploymentTypeEditModal
      ref="typeEditModalRef"
      v-model:visible="typeEditModalVisible"
      :saving="mutating"
      :save-error="typeSaveError"
      @save="handleTypeEditSave"
    />

    <EmployingCompanyEditModal
      ref="companyEditModalRef"
      v-model:visible="companyEditModalVisible"
      :saving="mutating"
      :save-error="companySaveError"
      :type-options="companyTypeSelectOptions"
      @save="handleCompanyEditSave"
    />
  </div>
</template>

<script setup>
import { useSettingsDraftGuard } from '../../composables/useSettingsDraftGuard';
import { matchesSearchText } from '@/utils/searchText';
import { useSettingsLoadGuard } from '../../composables/useSettingsLoadGuard';
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import FloatLabel from 'primevue/floatlabel';
import MultiSelect from 'primevue/multiselect';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Message from 'primevue/message';
import ProgressSpinner from 'primevue/progressspinner';
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import TabPanels from 'primevue/tabpanels';
import TabPanel from 'primevue/tabpanel';
import SettingsSectionHeader from '../SettingsSectionHeader.vue';
import SettingsOrgReferenceToolbar from '../SettingsOrgReferenceToolbar.vue';
import SettingsOrgReferenceStatusTag from '../SettingsOrgReferenceStatusTag.vue';
import EmploymentTypeEditModal from '../modals/EmploymentTypeEditModal.vue';
import EmployingCompanyEditModal from '../modals/EmployingCompanyEditModal.vue';
import { useSettingsEmploymentReferenceList } from '../../composables/useSettingsEmploymentReferenceList';
import {
  buildOrgReferenceCountLabel,
  isOrgReferenceActive,
  orgReferenceRowClass,
  sortOrgReferenceRows,
} from '../../utils/settingsOrgReferenceUtils';
import { employmentReferenceApi } from '@/api/employmentReferenceApi';
import { useClientTableSort } from '@/utils/tableSort';

defineOptions({ name: 'SettingsEmploymentReferenceSection' });

const {
  sortField: typeSortField,
  sortOrder: typeSortOrder,
  onSort: onTypeSort,
} = useClientTableSort('label');
const {
  sortField: companySortField,
  sortOrder: companySortOrder,
  onSort: onCompanySort,
} = useClientTableSort('name');

defineProps({
  section: {
    type: Object,
    default: null,
  },
});

defineEmits(['close-settings']);

const toast = useToast();
const confirm = useConfirm();
const { showInactive, fetchOptions, afterMutation } = useSettingsEmploymentReferenceList();

const activeTab = ref('types');
const employmentTypes = ref([]);
const companies = ref([]);
const loading = ref(false);
const beginLoad = useSettingsLoadGuard();
const error = ref('');
const typeCreating = ref(false);
const companyCreating = ref(false);
const mutating = ref(false);
const typeSaveError = ref(null);
const companySaveError = ref(null);
const typeSearchQuery = ref('');
const companySearchQuery = ref('');
const typeEditModalVisible = ref(false);
const companyEditModalVisible = ref(false);
const typeEditModalRef = ref(null);
const companyEditModalRef = ref(null);

const typeQuickAdd = reactive({
  code: '',
  label: '',
});

const companyQuickAdd = reactive({
  name: '',
  employment_type_codes: [],
});

const activeTypeSelectOptions = computed(() =>
  employmentTypes.value
    .filter(isOrgReferenceActive)
    .map((type) => ({ label: type.label, value: type.code }))
);

const companyTypeSelectOptions = computed(() => {
  const map = new Map();
  employmentTypes.value.forEach((type) => {
    map.set(type.code, { label: type.label, value: type.code });
  });
  // Keep linked codes selectable when list is active-only but company still references inactive types.
  companies.value.forEach((company) => {
    (company.employment_type_codes || []).forEach((code) => {
      const key = String(code || '').trim();
      if (key && !map.has(key)) {
        map.set(key, { label: `${key} (inactive)`, value: key });
      }
    });
  });
  return [...map.values()];
});

const filterRows = (items, query, fields) => {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return items;
  return items.filter((item) => {
    const haystack = [
      ...fields.map((field) => item[field]),
      ...(Array.isArray(item.employment_type_codes) ? item.employment_type_codes : []),
      isOrgReferenceActive(item) ? 'active' : 'inactive',
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return matchesSearchText([haystack], normalized);
  });
};

const filteredTypes = computed(() => {
  const base = filterRows(employmentTypes.value, typeSearchQuery.value, ['label', 'code']);
  return sortOrgReferenceRows(base, { sortBy: 'label' });
});

const filteredCompanies = computed(() => {
  const base = filterRows(companies.value, companySearchQuery.value, ['name']);
  return sortOrgReferenceRows(base);
});

const typesCountLabel = computed(() =>
  buildOrgReferenceCountLabel(employmentTypes.value, 'employment types', showInactive.value)
);

const companiesCountLabel = computed(() =>
  buildOrgReferenceCountLabel(companies.value, 'companies', showInactive.value)
);

const typesEmptyMessage = computed(() => {
  if (typeSearchQuery.value) return 'No employment types match your search.';
  if (showInactive.value) return 'No employment types found. Use quick add above to create one.';
  return 'No active employment types yet. Use quick add above to create one.';
});

const companiesEmptyMessage = computed(() => {
  if (companySearchQuery.value) return 'No companies match your search.';
  if (showInactive.value) return 'No companies found. Use quick add above to create one.';
  return 'No active companies yet. Use quick add above to create one.';
});

const rowClass = (data) => orgReferenceRowClass(data);

const formatCompanyTypes = (company) => {
  const codes = company?.employment_type_codes || [];
  if (!codes.length) return '—';
  return codes.join(', ');
};

const notifyMutationSuccess = (detail) => {
  toast.add({
    severity: 'success',
    summary: 'Success',
    detail,
    life: 2500,
  });
};

const afterMutationReload = () => afterMutation(loadReference);

const loadReference = async () => {
  const isCurrent = beginLoad();
  loading.value = true;
  error.value = '';
  try {
    const options = await fetchOptions();
    if (!isCurrent()) return;
    employmentTypes.value = sortOrgReferenceRows(
      Array.isArray(options?.employment_types) ? options.employment_types : [],
      { sortBy: 'label' }
    );
    companies.value = sortOrgReferenceRows(
      Array.isArray(options?.companies) ? options.companies : [],
    );
  } catch (err) {
    if (!isCurrent()) return;
    error.value = err?.message || 'Failed to load employment reference data.';
    employmentTypes.value = [];
    companies.value = [];
  } finally {
    if (isCurrent()) loading.value = false;
  }
};

const handleTypeQuickAdd = async () => {
  const code = typeQuickAdd.code?.trim();
  if (!code || typeCreating.value) return;

  typeCreating.value = true;
  try {
    await employmentReferenceApi.types.create({
      code,
      label: typeQuickAdd.label?.trim() || code,
    });
    typeQuickAdd.code = '';
    typeQuickAdd.label = '';
    notifyMutationSuccess('Employment type created');
    await afterMutationReload();
  } catch (err) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: err?.message || 'Failed to create employment type.',
      life: 3500,
    });
  } finally {
    typeCreating.value = false;
  }
};

const handleCompanyQuickAdd = async () => {
  const name = companyQuickAdd.name?.trim();
  if (!name || !companyQuickAdd.employment_type_codes.length || companyCreating.value) return;

  companyCreating.value = true;
  try {
    await employmentReferenceApi.companies.create({
      name,
      employment_type_codes: [...companyQuickAdd.employment_type_codes],
    });
    companyQuickAdd.name = '';
    companyQuickAdd.employment_type_codes = [];
    notifyMutationSuccess('Company created');
    await afterMutationReload();
  } catch (err) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: err?.message || 'Failed to create company.',
      life: 3500,
    });
  } finally {
    companyCreating.value = false;
  }
};

const openTypeEdit = (row) => {
  typeSaveError.value = null;
  typeEditModalRef.value?.openEdit?.(row);
  typeEditModalVisible.value = true;
};

const openCompanyEdit = (row) => {
  companySaveError.value = null;
  companyEditModalRef.value?.openEdit?.(row);
  companyEditModalVisible.value = true;
};

const handleTypeEditSave = async (payload) => {
  if (mutating.value) return;
  typeSaveError.value = null;
  mutating.value = true;
  try {
    await employmentReferenceApi.types.update(payload);
    typeEditModalVisible.value = false;
    notifyMutationSuccess('Employment type updated');
    await afterMutationReload();
  } catch (err) {
    typeSaveError.value = { message: err?.message || 'Failed to save changes.' };
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: err?.message || 'Failed to update employment type.',
      life: 3500,
    });
  } finally {
    mutating.value = false;
  }
};

const handleCompanyEditSave = async (payload) => {
  if (mutating.value) return;
  companySaveError.value = null;
  mutating.value = true;
  try {
    await employmentReferenceApi.companies.update(payload);
    companyEditModalVisible.value = false;
    notifyMutationSuccess('Company updated');
    await afterMutationReload();
  } catch (err) {
    companySaveError.value = { message: err?.message || 'Failed to save changes.' };
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: err?.message || 'Failed to update company.',
      life: 3500,
    });
  } finally {
    mutating.value = false;
  }
};

const confirmTypeDeactivate = (row) => {
  confirm.require({
    header: 'Deactivate employment type',
    message: `"${row.label}" will be hidden from employee create/edit dropdowns. Existing employee records keep their value.`,
    icon: 'pi pi-exclamation-triangle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Deactivate',
    acceptClass: 'p-button-warning',
    accept: () => deactivateType(row),
  });
};

const deactivateType = async (row) => {
  if (mutating.value) return;
  mutating.value = true;
  try {
    await employmentReferenceApi.types.deactivate({
      id: row.id,
      code: row.code,
      label: row.label,
    });
    notifyMutationSuccess('Employment type deactivated');
    await afterMutationReload();
  } catch (err) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: err?.message || 'Failed to deactivate employment type.',
      life: 3500,
    });
  } finally {
    mutating.value = false;
  }
};

const confirmTypeDelete = (row) => {
  const inUse = Number(row.employee_count ?? 0) > 0;
  confirm.require({
    header: inUse ? 'Remove employment type' : 'Delete employment type',
    message: inUse
      ? `"${row.label}" is used by ${row.employee_count} employee(s). It will be deactivated instead of deleted.`
      : `Permanently delete "${row.label}"? This cannot be undone.`,
    icon: 'pi pi-exclamation-triangle',
    rejectLabel: 'Cancel',
    acceptLabel: inUse ? 'Deactivate' : 'Delete',
    acceptClass: inUse ? 'p-button-warning' : 'p-button-danger',
    accept: () => deleteType(row),
  });
};

const deleteType = async (row) => {
  if (mutating.value) return;
  mutating.value = true;
  try {
    const result = await employmentReferenceApi.types.delete({ id: row.id });
    const mode = result?.mode === 'deactivated' ? 'deactivated' : 'deleted';
    notifyMutationSuccess(`Employment type ${mode}`);
    await afterMutationReload();
  } catch (err) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: err?.message || 'Failed to delete employment type.',
      life: 3500,
    });
  } finally {
    mutating.value = false;
  }
};

const confirmCompanyDeactivate = (row) => {
  confirm.require({
    header: 'Deactivate company',
    message: `"${row.name}" will be hidden from employee create/edit dropdowns. Existing employee records keep their value.`,
    icon: 'pi pi-exclamation-triangle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Deactivate',
    acceptClass: 'p-button-warning',
    accept: () => deactivateCompany(row),
  });
};

const deactivateCompany = async (row) => {
  if (mutating.value) return;
  mutating.value = true;
  try {
    await employmentReferenceApi.companies.deactivate({
      id: row.id,
      name: row.name,
      employment_type_codes: [...(row.employment_type_codes || [])],
    });
    notifyMutationSuccess('Company deactivated');
    await afterMutationReload();
  } catch (err) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: err?.message || 'Failed to deactivate company.',
      life: 3500,
    });
  } finally {
    mutating.value = false;
  }
};

const confirmCompanyDelete = (row) => {
  const inUse = Number(row.employee_count ?? 0) > 0;
  confirm.require({
    header: inUse ? 'Remove company' : 'Delete company',
    message: inUse
      ? `"${row.name}" is used by ${row.employee_count} employee(s). It will be deactivated instead of deleted.`
      : `Permanently delete "${row.name}"? This cannot be undone.`,
    icon: 'pi pi-exclamation-triangle',
    rejectLabel: 'Cancel',
    acceptLabel: inUse ? 'Deactivate' : 'Delete',
    acceptClass: inUse ? 'p-button-warning' : 'p-button-danger',
    accept: () => deleteCompany(row),
  });
};

const deleteCompany = async (row) => {
  if (mutating.value) return;
  mutating.value = true;
  try {
    const result = await employmentReferenceApi.companies.delete({ id: row.id });
    const mode = result?.mode === 'deactivated' ? 'deactivated' : 'deleted';
    notifyMutationSuccess(`Company ${mode}`);
    await afterMutationReload();
  } catch (err) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: err?.message || 'Failed to delete company.',
      life: 3500,
    });
  } finally {
    mutating.value = false;
  }
};

onMounted(() => {
  loadReference();
});

watch(showInactive, () => {
  loadReference();
});
useSettingsDraftGuard({ draft: {}, saving: () => mutating.value || typeCreating.value || companyCreating.value });
useSettingsDraftGuard({ draft: typeQuickAdd, saving: () => typeCreating.value });
useSettingsDraftGuard({ draft: companyQuickAdd, saving: () => companyCreating.value });
</script>

<style scoped>
.settings-employment-ref-section {
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
}

.settings-employment-ref-section__body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 1rem 1.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.settings-employment-ref-section__state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 12rem;
}

.settings-employment-ref-section__message {
  margin: 0;
}

.settings-employment-ref-section__tabs {
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
}

.settings-employment-ref-section__tablist {
  margin-bottom: 0.75rem;
}

.settings-employment-ref-section__panel {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.settings-employment-ref-section__quick-add {
  padding: 1rem;
  border: 1px solid var(--crc-panel-border, var(--surface-border));
  border-radius: 0.75rem;
  background: var(--crc-panel-bg);
}

.settings-employment-ref-section__quick-add-title {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--crc-text-muted, var(--text-color-secondary));
  margin-bottom: 0.75rem;
}

.settings-employment-ref-section__quick-add-form {
  display: grid;
  gap: 0.75rem;
  align-items: end;
}

.settings-employment-ref-section__quick-add-form--types {
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto;
}

.settings-employment-ref-section__quick-add-form--companies {
  grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr) auto;
}

.settings-employment-ref-section__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 10rem;
  border: 1px dashed var(--crc-panel-border, var(--surface-border));
  border-radius: 0.75rem;
  color: var(--crc-text-muted, var(--text-color-secondary));
  text-align: center;
}

.settings-employment-ref-section__empty-icon {
  font-size: 1.5rem;
}

.settings-employment-ref-section__empty p {
  margin: 0;
  font-size: 0.875rem;
}

:deep(.settings-org-section__row--inactive) {
  opacity: 0.55;
}

@media (max-width: 768px) {
  .settings-employment-ref-section__quick-add-form--types,
  .settings-employment-ref-section__quick-add-form--companies {
    grid-template-columns: 1fr;
  }
}
</style>
